// Celebration queue store — Sprint 1 §11.3.
//
// The reinforcement / quiz / flashcards / game views push a celebration
// payload here when the API response includes a `celebration` object with
// fired=true. The <CelebrationOverlay /> mounted at the App root reads from
// here, plays one celebration at a time, and dismisses.
//
// We don't try to render multiple stacked celebrations — if two events fire
// in quick succession the second is enqueued. The api also rate-limits the
// `activity_complete` event server-side so the queue typically stays at 0 or 1.
//
// Reduced-motion users: if window.matchMedia('(prefers-reduced-motion)')
// matches, we still acknowledge the celebration in state (so the API caller
// can branch on it) but the overlay renders a static "Nice work" pill instead
// of an animation.

import { defineStore } from 'pinia'

export type CelebrationStyle =
  | 'confetti'
  | 'fireworks'
  | 'balloons'
  | 'sparkles'
  | 'stars'
  | 'hearts'
  | 'applause'
  | 'trophy'

export interface CelebrationPayload {
  kind: string
  asset_url: string | null
  asset_kind: string | null
  intensity: number
  /** When asset_url is null, the overlay picks a built-in animation by this
   *  name. When both are null the overlay rotates randomly. */
  style?: CelebrationStyle | string | null
}

export const BUILTIN_STYLES: CelebrationStyle[] = [
  'confetti', 'fireworks', 'balloons', 'sparkles',
  'stars', 'hearts', 'applause', 'trophy',
]

/** Pick a random built-in style when none was specified. */
export function pickRandomStyle(): CelebrationStyle {
  return BUILTIN_STYLES[Math.floor(Math.random() * BUILTIN_STYLES.length)]
}

interface State {
  queue: CelebrationPayload[]
  current: CelebrationPayload | null
}

export const useCelebrationStore = defineStore('celebration', {
  state: (): State => ({
    queue: [],
    current: null,
  }),
  actions: {
    enqueue(payload: CelebrationPayload | null | undefined) {
      if (!payload) return
      // If nothing playing, hoist immediately.
      if (this.current === null) {
        this.current = payload
        return
      }
      // Skip duplicates of the same kind already queued/playing.
      if (this.current?.kind === payload.kind) return
      if (this.queue.some((q) => q.kind === payload.kind)) return
      this.queue.push(payload)
    },
    dismiss() {
      this.current = this.queue.shift() ?? null
    },
    clear() {
      this.current = null
      this.queue = []
    },
  },
})

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}
